'use client'

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from '@mui/material'
import { Mail, ArrowBack } from '@mui/icons-material'
import { useSpring, animated, config } from 'react-spring'

import AuthService from './auth.service'
import AlertService from '../../components/Alerts/alerts'
import ConfigurationService from '../Configuration/configuration.service'
import backgroundImage from '../../assets/backgroundImage.png';

export default function ForgotPassword() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [logo, setLogo] = useState(localStorage.getItem('system_logo'))
  const [email, setEmail] = useState('')
  const [theme, setTheme] = useState(createTheme())

  const isMobile = useMediaQuery('(max-width:600px)')
  const isTablet = useMediaQuery('(max-width:960px)')

  const fadeIn = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: config.molasses,
  })

  const slideUp = useSpring({
    transform: 'translateY(0px)',
    from: { transform: 'translateY(50px)' },
    config: config.wobbly,
  })

  useEffect(() => {
    getSetLanguage()
    getSystemConfig()
  }, [])

  const getSetLanguage = () => {
    const language = localStorage.getItem('language') || 'en'
    i18n.changeLanguage(language)
    localStorage.setItem('language', language)
  }

  const getSystemConfig = async () => {
    try {
      const response = await ConfigurationService.getConfiguration()
      if (response.data.status) {
        const config = response.data.data[0]
        setColors(config.buttons_color, '--buttons-color')
        setColors(config.topbar_color, '--topbar-color')
        setColors(config.leftbar_color, '--leftbar-color')
        setColors(config.buttons_text_color, '--buttons-text-color')
        setSystemLogo(config.system_logo)

        setTheme(createTheme({
          palette: {
            primary: {
              main: config.buttons_color,
            },
            secondary: {
              main: config.topbar_color,
            },
          },
        }))
      } else {
        setColors('#3488A4', 'onerror')
      }
    } catch (error) {
      setColors('#3488A4', 'onerror')
    }
  }

  const setColors = (color, param) => {
    localStorage.setItem(param.slice(2), color)
    document.documentElement.style.setProperty(param, color)
  }

  const setSystemLogo = (sys_logo) => {
    setLogo(sys_logo)
    localStorage.setItem('system_logo', sys_logo)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!email) {
      AlertService.swalErrorAlert(t('Please enter your email'))
      return
    }

    setLoading(true)
    try {
      const response = await AuthService.pwresetreq(email)
      setLoading(false)
      if (response.data.status) {
        localStorage.setItem('resetpass', 'true')
        navigate('/customer_reset_password')
        AlertService.successToast(t('Email is sent successfully. Please check your email and reset your password.'))
      } else {
        AlertService.swalErrorAlert(response.data.message || t("Error: Can't send reset code at the moment. Please try again later."))
      }
    } catch (error) {
      setLoading(false)
      AlertService.swalErrorAlert(t('Server Error, Please try again later.'))
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(to left, rgba(255,255,255,0.3), rgba(255,255,255,0.1)), url(${backgroundImage})`,
          backgroundSize: 'cover', 
          p: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <animated.div style={fadeIn}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                backgroundColor: '#fff',
                borderRadius: { xs: '12px', sm: '20px' },
                overflow: 'hidden',
                boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                height: { xs: 'auto', md: '500px' },
              }}
            >
              {/* Left Side - Forgot Password Form */}
              <Box
                sx={{
                  flex: 1,
                  p: { xs: 3, sm: 4, md: 6 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 2, sm: 3 }
                }}
              >
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(-1)}
                  sx={{
                    marginTop:'-5%',
                    alignSelf: 'flex-start',
                    color: theme.palette.text.secondary,
                  }}
                >
                  Back
                </Button>

                {/* Logo */}
                <animated.div style={slideUp}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      alignSelf: { xs: 'center', sm: 'flex-start' }
                    }}
                  >
                    <Box
                      component="img"
                      src={logo}
                      alt="Logo"
                      sx={{
                        width: { xs: 40, sm: 50, md: 60 },
                        height: { xs: 40, sm: 50, md: 60 },
                        borderRadius: 1,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                      }}
                    >
                      IPHRx
                    </Typography>
                  </Box>
                </animated.div>

                <animated.div style={slideUp}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: theme.palette.primary.main,
                      mb: { xs: 1, sm: 2, md: 3 },
                      fontWeight: 500,
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}
                  >
                    Forgot Password
                  </Typography>
                </animated.div>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: 'center',
                    mb: 2
                  }}
                >
                  Enter your email to reset your password.
                </Typography>

                {/* Forgot Password Form */}
                <Box
                  component="form"
                  onSubmit={onSubmit}
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 400 }
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      mb: { xs: 2, sm: 3 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        '& fieldset': { border: 'none' },
                        borderRadius: '50px',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <Mail sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 24 }, mr: 1 }} />
                      ),
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                      py: { xs: 1, sm: 1.5 },
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '50px',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </Box>

              </Box>

              {/* Right Side - Decorative */}
              <Box
                sx={{
                  flex: 1,
                  bgcolor: theme.palette.primary.main,
                  p: { xs: 4, sm: 6 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: { xs: '200px', md: 'auto' },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: { xs: '200px', sm: '300px' },
                    height: { xs: '200px', sm: '300px' },
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                    transform: 'translate(50%, -50%) rotate(45deg)',
                    borderRadius: '50px'
                  }
                }}
              >
                <animated.div style={slideUp}>
                  <Typography
                    variant="h3"
                    sx={{
                      mb: { xs: 2, sm: 3 },
                      fontWeight: 500,
                      fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                      textAlign: 'center'
                    }}
                  >
                    Reset Your Password
                  </Typography>
                </animated.div>
                <animated.div style={slideUp}>
                  <Typography
                    textAlign="center"
                    sx={{
                      mb: { xs: 3, sm: 4 },
                      maxWidth: 400,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      px: { xs: 2, sm: 0 }
                    }}
                  >
                    Don't worry, we've got you covered. Follow the instructions we'll send to your email to reset your password.
                  </Typography>
                </animated.div>
                <Button
                  sx={{
                    mt: { xs: 2, sm: 3 },
                    color: theme.palette.primary.main,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    }
                  }}
                  onClick={() => navigate('/customer_login')}
                >
                  Back to Login
                </Button>

              </Box>
            </Box>
          </animated.div>
        </Container>
      </Box>
    </ThemeProvider>
  )
}